import { __rest } from './../../external/tslib/tslib.es6.js';
import React, { useRef, useState, useEffect } from 'react';
import { Container, Wrapper, Overline, Title, Content, CloseIcon } from './styles.js';
import { debounce, hasKeyInDirection, getInvertedTopOrBottomDirection } from './utils.js';
import { Flex } from '../Flex/Flex.js';

function Popover(_a) {
    var { active = false, children, content, delay = 300, disableClickAway = false, hasCloseButton = false, onClose, onOpen, placement = 'bottom', title, overline, trigger = 'click', variation = 'neutral' } = _a, props = __rest(_a, ["active", "children", "content", "delay", "disableClickAway", "hasCloseButton", "onClose", "onOpen", "placement", "title", "overline", "trigger", "variation"]);
    const refPopover = useRef(null);
    const [isOpen, setIsOpen] = useState(active);
    const [placementInverter, setPlacementInverter] = useState(null);
    const shouldDisableClickAway = active || disableClickAway;
    const toggleOpen = debounce((value) => setIsOpen(value), delay);
    if (refPopover.current) {
        refPopover.current.toggleOpen = (value) => toggleOpen(value);
    }
    const handleClose = () => {
        if (isOpen) {
            toggleOpen(false);
            if (onClose)
                onClose();
        }
    };
    const handleOpen = () => {
        if (!isOpen) {
            toggleOpen(true);
            if (onOpen)
                onOpen();
        }
    };
    const handleClickAway = ({ target }) => {
        const shouldClose = (target) => isOpen && refPopover.current && !refPopover.current.contains(target);
        if (shouldClose(target)) {
            handleClose();
        }
    };
    const handleTrigger = () => {
        switch (trigger) {
            case 'hover':
                return {
                    onMouseEnter: () => handleOpen(),
                    onMouseLeave: () => handleClose(),
                };
            case 'click':
            default:
                return {
                    onClick: (event) => {
                        handleOpen();
                        handleClickAway(event);
                    },
                };
        }
    };
    const getPlacementInverted = () => {
        const hasDirectionInvert = hasKeyInDirection(placement);
        if (hasDirectionInvert && isOpen && !placementInverter) {
            setTimeout(() => {
                var _a;
                const elementMeasures = (_a = refPopover === null || refPopover === void 0 ? void 0 : refPopover.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                const newPlacementInverter = getInvertedTopOrBottomDirection(elementMeasures, placement);
                if (newPlacementInverter !== placementInverter) {
                    setPlacementInverter(getInvertedTopOrBottomDirection(elementMeasures, placement));
                }
            }, 0);
        }
        if (hasDirectionInvert && !isOpen && placementInverter) {
            setPlacementInverter(null);
        }
    };
    useEffect(() => {
        getPlacementInverted();
        if (!shouldDisableClickAway && isOpen) {
            document.addEventListener('click', handleClickAway);
        }
        else {
            document.removeEventListener('click', handleClickAway);
        }
        return () => {
            document.removeEventListener('click', handleClickAway);
        };
    }, [isOpen]);
    const isContentAlone = !overline && !title;
    return (React.createElement(Container, Object.assign({}, handleTrigger()),
        React.createElement(Wrapper, Object.assign({}, props, { placement: placementInverter || placement, isOpen: isOpen, ref: refPopover, variation: variation }),
            React.createElement(Flex, { justifyContent: "space-between", w: "100%" },
                React.createElement("div", null,
                    overline && React.createElement(Overline, null, overline),
                    title && React.createElement(Title, null, title),
                    isContentAlone ? React.createElement(Content, null, content) : null),
                trigger === 'click' && hasCloseButton && (React.createElement(CloseIcon, { onClick: handleClose }))),
            !isContentAlone ? React.createElement(Content, null, content) : null),
        children));
}

export { Popover as default };
